import { useMemo, useEffect } from 'react';
import { getOptionsForAgents } from 'app/stores/synthetics/utils';
import { SynStore } from 'app/stores/synthetics/synthTypes';

export interface AgentOptionData {
  value: string;
  label: string;
  agentIcon?: React.ComponentType;
  locationInfo?: React.ReactNode;
  status?: { icon?: string; color?: string; text?: string };
  [key: string]: unknown;
}

interface UseAgentOptionsResult {
  options: AgentOptionData[] | null;
  isLoading: boolean;
}

/**
 * Hook to fetch and transform agent options for use in filter dropdowns.
 * Handles fetching agents from the $syn store and transforming them into
 * options with rich metadata for BaseAgentRenderer.
 */
export function useAgentOptions($syn: SynStore | undefined, enabled: boolean): UseAgentOptionsResult {
  const agents = $syn?.agents;
  const isLoading = enabled && agents?.requestStatus === 'fetching';
  const needsFetch = enabled && agents && !agents.hasFetched && !agents.requestStatus;

  // Fetch agents when needed
  useEffect(() => {
    if (needsFetch) {
      agents?.fetch();
    }
  }, [needsFetch, agents]);

  // Transform agents to options with rich metadata
  const options = useMemo(() => {
    if (!enabled || !agents) {
      return null;
    }

    const allAgents = agents.get();
    if (!Array.isArray(allAgents)) {
      return [];
    }

    // getOptionsForAgents returns objects with value, label, icon, locationInfo, status, etc.
    const rawOptions = getOptionsForAgents(allAgents);

    // Map options to store icon in agentIcon to prevent CoreSelect from rendering it
    // (CoreSelect auto-renders any 'icon' property, but we want BaseAgentRenderer to handle it)
    return rawOptions.map(({ icon, ...rest }: { icon?: unknown; [key: string]: unknown }) => ({
      ...rest,
      agentIcon: icon
    })) as AgentOptionData[];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, agents, agents?.hasFetched]);

  return {
    options,
    isLoading: isLoading || needsFetch || false
  };
}
